import { userJourneyMap as codes } from "@sisuwellness/utilities";
import { LifestyleGuidelineDefinition } from "../Guidelines/Lifestyle/SiSU";

export default class Recommendations {
    /**
     * Returns a recommendation based on the given ad slot code, criteria, and replacements.
     * @param {string} adSlotCode - The code for the ad slot.
     * @param {object} criteria - The criteria for the recommendation.
     * @returns {string} - The recommendation.
     */
    static getRecommendationFromAdSlotCode(adSlotCode, criteria) {
        const recommendation = {
            BMI_RESULTS_SCREEN_RECOMMENDATION: Recommendations._getBmiRecommendation(criteria),
            BP_RESULTS_SCREEN_RECOMMENDATION: Recommendations._getBpRecommendation(criteria),
            HR_RESULTS_SCREEN_RECOMMENDATION: Recommendations._getHrRecommendation(criteria),
            NHS_BP_RESULTS_SCREEN_RECOMMENDATION: Recommendations._getNhsBpRecommendation(criteria),
            NHS_HR_RESULTS_SCREEN_RECOMMENDATION: Recommendations._getNhsHrRecommendation(criteria),
            BF_RESULTS_SCREEN_RECOMMENDATION: Recommendations._getBodyFatRecommendation(criteria),
            DIABETES_RESULTS_SCREEN_RECOMMENDATION: Recommendations._getDiabetesRiskRecommendation(criteria),
            UK_DIABETES_RESULTS_SCREEN_RECOMMENDATION: Recommendations._getUKDiabetesRiskRecommendation(criteria),
            HRA_SMOKING: Recommendations._getSmokingRecommendation(criteria),
            HRA_WAIST_CIRCUMFERENCE: Recommendations._getWaistCircumferenceRecommendation(criteria),
            HRA_TOTAL_CHOLESTEROL: Recommendations._getTotalCholesterolRecommendation(criteria),
            HRA_FASTING_GLUCOSE: Recommendations._getFastingGlucoseRecommendation(criteria),
            NON_FASTING_GLUCOSE: Recommendations._getNonFastingGlucoseRecommendation(criteria),
            HDL_CHOLESTEROL: Recommendations._getHdlCholesterolRecommendation(criteria),
            [codes.adSlots.qriskResultPageRecommendations.code]: Recommendations._getQriskRecommendation(criteria),
            [codes.adSlots.alcoholResultPageRecommendations.code]: Recommendations._getAlcoholRecommendation(criteria),
            [codes.adSlots.activityResultPageRecommendations.code]: Recommendations._getActivityRecommendation(
                criteria
            ),
            [codes.adSlots.stressPSS4ResultPageRecommendations.code]: Recommendations._getStressPss4Recommendation(
                criteria
            ),
            [codes.adSlots.stressGad2ResultPageRecommendations.code]: Recommendations._getStressGad2Recommendation(
                criteria
            ),
            [codes.adSlots.stressPhq2ResultPageRecommendations.code]: Recommendations._getStressPhq2Recommendation(
                criteria
            ),
            [codes.adSlots.heartAgeResultPageRecommendations.code]: Recommendations._getHeartAgeRecommendation(
                criteria
            ),
            [codes.adSlots.dailyEnergyUseResultPageRecommendations
                .code]: Recommendations._getDailyEnergyUseRecommendation(criteria),
            [codes.adSlots.muscleMassResultPageRecommendations.code]: Recommendations._getMuscleMassRecommendation(
                criteria
            ),
            [codes.adSlots.wellbeingWHO5ResultPageRecommendations
                .code]: Recommendations._getWellbeingWHO5Recommendation(criteria),
            [codes.adSlots.mskResultPageRecommendations.code]: Recommendations._getMskRecommendation(criteria),
            [codes.adSlots.womensHealthResultPageRecommendations.code]: Recommendations._getWomensHealthRecommendation(
                criteria
            ),
            [codes.adSlots.gppaqResultPageRecommendations.code]: Recommendations._getGppaqRecommendation(criteria),
            [codes.adSlots.lifestyleResultPageRecommendations.code]: Recommendations._getLifestyleRecommendation(
                criteria
            ),
            [codes.adSlots.lifestyleAreaRecommendations.code]: Recommendations._getLifestyleAreaRecommendation(criteria)
        };

        return recommendation[adSlotCode];
    }

    static getRecommendationFromRiskCode(riskCode, criteria) {
        const recommendation = {
            BODY_MASS_INDEX: Recommendations._getBmiRecommendation(criteria),
            BLOOD_PRESSURE: Recommendations._getBpRecommendation(criteria),
            HEART_RATE: Recommendations._getHrRecommendation(criteria),
            NHS_BLOOD_PRESSURE: Recommendations._getNhsBpRecommendation(criteria),
            NHS_HEART_RATE: Recommendations._getNhsHrRecommendation(criteria),
            BODY_FAT: Recommendations._getBodyFatRecommendation(criteria),
            DIABETES: Recommendations._getDiabetesRiskRecommendation(criteria),
            SMOKING: Recommendations._getSmokingRecommendation(criteria),
            WAIST_CIRCUMFERENCE: Recommendations._getWaistCircumferenceRecommendation(criteria),
            TOTAL_CHOLESTEROL: Recommendations._getTotalCholesterolRecommendation(criteria),
            FASTING_GLUCOSE: Recommendations._getFastingGlucoseRecommendation(criteria),
            NON_FASTING_GLUCOSE: Recommendations._getNonFastingGlucoseRecommendation(criteria),
            HDL_CHOLESTEROL: Recommendations._getHdlCholesterolRecommendation(criteria),
            QRISK3: Recommendations._getQriskRecommendation(criteria),
            ALCOHOL_RISK: Recommendations._getAlcoholRecommendation(criteria),
            ACTIVITY: Recommendations._getActivityRecommendation(criteria),
            STRESS_PSS4: Recommendations._getStressPss4Recommendation(criteria),
            STRESS_GAD2: Recommendations._getStressGad2Recommendation(criteria),
            STRESS_PHQ2: Recommendations._getStressPhq2Recommendation(criteria),
            HEART_AGE: Recommendations._getHeartAgeRecommendation(criteria),
            DAILY_ENERGY_USE: Recommendations._getDailyEnergyUseRecommendation(criteria),
            WELLBEING_WHO5: Recommendations._getWellbeingWHO5Recommendation(criteria),
            MSK: Recommendations._getMskRecommendation(criteria),
            WOMENS_HEALTH: Recommendations._getWomensHealthRecommendation(criteria),
            GPPAQ: Recommendations._getGppaqRecommendation(criteria),
            LIFESTYLE: Recommendations._getLifestyleRecommendation(criteria)
        };

        return recommendation[riskCode];
    }

    /**
     * BMI
     *
     * These keys are the single source of the i18n key for BOTH surfaces: the station
     * overwrites the CampaignAd's seeded English prose with this value before rendering
     * (adSlotView), and the members portal does the equivalent in utilities/Recommendation.
     * Changing a key here changes it everywhere.
     *
     * The `v2` namespace exists to decouple the code deploy from the SimpleLocalize publish.
     * v2 copy states a weight RANGE and carries {{HEALTHY_WEIGHT_LOWER}}/{{HEALTHY_WEIGHT_UPPER}};
     * v1 copy stated a single target and carried {{HEALTHY_BMI_WEIGHT}}. i18next renders an
     * unmatched token as literal text, so a rename-in-place would have forced station, members
     * portal and the mobile app to deploy in lockstep with the publish. With a new namespace,
     * v1 keys stay live for any consumer that has not shipped yet and each can deploy
     * independently.
     *
     * Retire v1 (delete the keys from all three SimpleLocalize projects) only once station,
     * members portal and the mobile app are all on v2.
     *
     * `recommendations.bmi.pregnancy` is deliberately NOT namespaced — it has no v1, so there
     * is no older consumer to protect and nothing to migrate.
     */
    static _getBmiRecommendation(criteria) {
        if (criteria.isPregnant) {
            return {
                recommendationText: "recommendations.bmi.pregnancy"
            };
        }

        return {
            low: {
                recommendationText: "recommendations.bmi.v2.low"
            },
            normal: {
                recommendationText: "recommendations.bmi.v2.normal"
            },
            elevated: {
                recommendationText: "recommendations.bmi.v2.elevated"
            },
            high: {
                recommendationText: "recommendations.bmi.v2.high"
            },
            veryHigh: {
                recommendationText: "recommendations.bmi.v2.veryHigh"
            }
        }[criteria.rating];
    }

    /**
     * Blood Pressure
     */
    static _getBpRecommendation(criteria) {
        return {
            notSure: {
                recommendationText: "recommendations.bp.notSure"
            },
            optimal: {
                recommendationText: "recommendations.bp.optimal"
            },
            normal: {
                recommendationText: "recommendations.bp.normal"
            },
            highNormal: {
                recommendationText: "recommendations.bp.highNormal"
            },
            mildHypertension: {
                recommendationText: "recommendations.bp.mildHypertension"
            },
            moderateHypertension: {
                recommendationText: "recommendations.bp.moderateHypertension"
            },
            severeHypertension: {
                recommendationText: "recommendations.bp.severeHypertension"
            },
            // for member portal, we don't have a flag to indicate if it was NHS or standard so we have to hack this in
            // but normal and highNormal will match the AU BP recommendations
            low: {
                recommendationText: "recommendations.nhs.bp.low"
            },
            high: {
                recommendationText: "recommendations.nhs.bp.high"
            },
            severe: {
                recommendationText: "recommendations.nhs.bp.severe"
            }
        }[criteria.rating];
    }

    /**
     * Heart Rate
     */
    static _getHrRecommendation(criteria) {
        return {
            low: {
                recommendationText: "recommendations.hr.low"
            },
            normal: {
                recommendationText: "recommendations.hr.normal"
            },
            elevated: {
                recommendationText: "recommendations.hr.elevated"
            },
            high: {
                recommendationText: "recommendations.hr.high"
            },
            veryHigh: {
                recommendationText: "recommendations.hr.veryHigh"
            }
        }[criteria.rating];
    }

    static _getNhsBpRecommendation(criteria) {
        return {
            notSure: {
                recommendationText: "recommendations.nhs.bp.notSure"
            },
            low: {
                recommendationText: "recommendations.nhs.bp.low"
            },
            normal: {
                recommendationText: "recommendations.nhs.bp.normal"
            },
            highNormal: {
                recommendationText: "recommendations.nhs.bp.highNormal"
            },
            high: {
                recommendationText: "recommendations.nhs.bp.high"
            },
            severe: {
                recommendationText: "recommendations.nhs.bp.severe"
            }
        }[criteria.rating];
    }

    static _getNhsHrRecommendation(criteria) {
        return {
            low: {
                recommendationText: "recommendations.nhs.hr.low"
            },
            normal: {
                recommendationText: "recommendations.nhs.hr.normal"
            },
            high: {
                recommendationText: "recommendations.nhs.hr.high"
            }
        }[criteria.rating];
    }

    /**
     * Body Fat
     */
    static _getBodyFatRecommendation(criteria) {
        return {
            low: {
                recommendationText: "recommendations.bodyFat.low"
            },
            normal: {
                recommendationText: "recommendations.bodyFat.normal"
            },
            high: {
                recommendationText: "recommendations.bodyFat.high"
            },
            veryHigh: {
                recommendationText: "recommendations.bodyFat.veryHigh"
            }
        }[criteria.rating];
    }

    /**
     * Diabetes Risk
     */
    static _getDiabetesRiskRecommendation(criteria) {
        return {
            low: {
                recommendationText: "recommendations.diabetesRisk.low"
            },
            intermediate: {
                recommendationText: "recommendations.diabetesRisk.intermediate"
            },
            high: {
                recommendationText: "recommendations.diabetesRisk.high"
            }
        }[criteria.rating];
    }

    /**
     * UK Diabetes Risk
     */
    static _getUKDiabetesRiskRecommendation(criteria) {
        return {
            low: {
                recommendationText: "recommendations.ukDiabetesRisk.low"
            },
            moderate: {
                recommendationText: "recommendations.ukDiabetesRisk.moderate"
            },
            high: {
                recommendationText: "recommendations.ukDiabetesRisk.high"
            },
            veryHigh: {
                recommendationText: "recommendations.ukDiabetesRisk.veryHigh"
            }
        }[criteria.rating];
    }

    /**
     * Smoking
     */
    static _getSmokingRecommendation(criteria) {
        const isSmokerRecommendation = {
            yes: {
                recommendationText: "recommendations.smoking.yes"
            },
            no: {
                recommendationText: "recommendations.smoking.no"
            }
        }[criteria.isSmoker];

        const smokingRatingRecommendation = {
            normal: {
                recommendationText: "recommendations.smoking.normal"
            },
            high: {
                recommendationText: "recommendations.smoking.high"
            }
        }[criteria.rating];

        // The API sometimes will not return a rating for smoking
        // Return the recommendation if it exists, otherwise return assume the user does not smoke
        return (
            isSmokerRecommendation ||
            smokingRatingRecommendation || { recommendationText: "recommendations.smoking.no" }
        );
    }

    /**
     * Waist Circumference
     */
    static _getWaistCircumferenceRecommendation(criteria) {
        return {
            high: {
                recommendationText: "recommendations.waistCircumference.high"
            }
        }[criteria.rating];
    }

    /**
     * Total Cholesterol
     */
    static _getTotalCholesterolRecommendation(criteria) {
        return {
            notSure: {
                recommendationText: "recommendations.totalCholesterol.notSure"
            },
            optimal: {
                recommendationText: "recommendations.totalCholesterol.optimal"
            },
            normal: {
                recommendationText: "recommendations.totalCholesterol.normal"
            },
            elevated: {
                recommendationText: "recommendations.totalCholesterol.elevated"
            },
            high: {
                recommendationText: "recommendations.totalCholesterol.high"
            }
        }[criteria.rating];
    }

    /**
     * Fasting Glucose
     */
    static _getFastingGlucoseRecommendation(criteria) {
        return {
            notSure: {
                recommendationText: "recommendations.fastingGlucose.notSure"
            },
            low: {
                recommendationText: "recommendations.fastingGlucose.low"
            },
            normal: {
                recommendationText: "recommendations.fastingGlucose.normal"
            },
            elevated: {
                recommendationText: "recommendations.fastingGlucose.elevated"
            },
            high: {
                recommendationText: "recommendations.fastingGlucose.high"
            }
        }[criteria.rating];
    }

    /**
     * Non Fasting Glucose
     */
    static _getNonFastingGlucoseRecommendation(criteria) {
        return {
            low: {
                recommendationText: "recommendations.nonFastingGlucose.low"
            },
            normal: {
                recommendationText: "recommendations.nonFastingGlucose.normal"
            },
            elevated: {
                recommendationText: "recommendations.nonFastingGlucose.elevated"
            },
            high: {
                recommendationText: "recommendations.nonFastingGlucose.high"
            }
        }[criteria.rating];
    }

    /**
     * HDL Cholesterol
     */
    static _getHdlCholesterolRecommendation(criteria) {
        return {
            low: {
                recommendationText: "recommendations.hdlCholesterol.low"
            },
            normal: {
                recommendationText: "recommendations.hdlCholesterol.normal"
            }
        }[criteria.rating];
    }

    /**
     * QRisk
     */
    static _getQriskRecommendation(criteria) {
        return {
            low: {
                recommendationText: "recommendations.qrisk.low.text",
                recommendationTextPortal: "recommendations.qrisk.low.textPortal"
            },
            elevated: {
                recommendationText: "recommendations.qrisk.elevated.text",
                recommendationTextPortal: "recommendations.qrisk.elevated.textPortal",
                url: "recommendations.qrisk.elevated.url"
            }
        }[criteria.rating];
    }

    /**
     * Alcohol
     */
    static _getAlcoholRecommendation(criteria) {
        return {
            noRisk: {
                recommendationText: "recommendations.alcohol.noRisk.text",
                recommendationTextPortal: "recommendations.alcohol.noRisk.textPortal"
            },
            low: {
                recommendationText: "recommendations.alcohol.low.text",
                recommendationTextPortal: "recommendations.alcohol.low.textPortal",
                url: "recommendations.alcohol.low.url"
            },
            elevated: {
                recommendationText: "recommendations.alcohol.elevated.text",
                recommendationTextPortal: "recommendations.alcohol.elevated.textPortal",
                url: "recommendations.alcohol.elevated.url"
            },
            high: {
                recommendationText: "recommendations.alcohol.high.text",
                recommendationTextPortal: "recommendations.alcohol.high.textPortal",
                url: "recommendations.alcohol.high.url"
            },
            veryHigh: {
                recommendationText: "recommendations.alcohol.veryHigh.text",
                recommendationTextPortal: "recommendations.alcohol.veryHigh.textPortal",
                url: "recommendations.alcohol.veryHigh.url"
            }
        }[criteria.rating];
    }

    /**
     * Activity
     */
    static _getActivityRecommendation(criteria) {
        return {
            active: {
                recommendationText: "recommendations.activity.active.text",
                recommendationTextPortal: "recommendations.activity.active.textPortal",
                url: "recommendations.activity.active.url"
            },
            moderatelyActive: {
                recommendationText: "recommendations.activity.moderatelyActive.text",
                recommendationTextPortal: "recommendations.activity.moderatelyActive.textPortal",
                url: "recommendations.activity.moderatelyActive.url"
            },
            moderatelyInactive: {
                recommendationText: "recommendations.activity.moderatelyInactive.text",
                recommendationTextPortal: "recommendations.activity.moderatelyInactive.textPortal",
                url: "recommendations.activity.moderatelyInactive.url"
            },
            inactive: {
                recommendationText: "recommendations.activity.inactive.text",
                recommendationTextPortal: "recommendations.activity.inactive.textPortal",
                url: "recommendations.activity.inactive.url"
            }
        }[criteria.rating];
    }

    /**
     * Stress PSS4
     */
    static _getStressPss4Recommendation(criteria) {
        return {
            low: {
                recommendationText: "recommendations.stressPss4.low.text",
                recommendationTextPortal: "recommendations.stressPss4.low.textPortal"
            },
            moderate: {
                recommendationText: "recommendations.stressPss4.moderate.text",
                recommendationTextPortal: "recommendations.stressPss4.moderate.textPortal"
            },
            high: {
                recommendationText: "recommendations.stressPss4.high.text",
                recommendationTextPortal: "recommendations.stressPss4.high.textPortal"
            }
        }[criteria.rating];
    }
    /**
     * Stress GAD2
     */
    static _getStressGad2Recommendation(criteria) {
        return {
            no_concern: {
                recommendationText: "recommendations.gad2.no_concern.text",
                recommendationTextPortal: "recommendations.gad2.no_concern.textPortal"
            },
            possible: {
                recommendationText: "recommendations.gad2.possible.text",
                recommendationTextPortal: "recommendations.gad2.possible.textPortal"
            }
        }[criteria.rating];
    }

    /**
     * Stress PHQ2
     */
    static _getStressPhq2Recommendation(criteria) {
        return {
            no_concern: {
                recommendationText: "recommendations.phq2.no_concern.text",
                recommendationTextPortal: "recommendations.phq2.no_concern.textPortal"
            },
            possible: {
                recommendationText: "recommendations.phq2.possible.text",
                recommendationTextPortal: "recommendations.phq2.possible.textPortal"
            }
        }[criteria.rating];
    }

    /**
     * Heart Age
     */
    static _getHeartAgeRecommendation(criteria) {
        return {
            optimal: {
                recommendationText: "recommendations.heartAge.optimal.text",
                recommendationTextPortal: "recommendations.heartAge.optimal.textPortal"
            },
            normal: {
                recommendationText: "recommendations.heartAge.normal.text",
                recommendationTextPortal: "recommendations.heartAge.normal.textPortal"
            },
            elevated: {
                recommendationText: "recommendations.heartAge.elevated.text",
                recommendationTextPortal: "recommendations.heartAge.elevated.textPortal"
            }
        }[criteria.rating];
    }

    /**
     * Daily Energy Use
     */
    static _getDailyEnergyUseRecommendation(criteria) {
        return {
            low: {
                recommendationText: "recommendations.dailyEnergyUse.low.text",
                recommendationTextPortal: "recommendations.dailyEnergyUse.low.textPortal"
            },
            moderate: {
                recommendationText: "recommendations.dailyEnergyUse.moderate.text",
                recommendationTextPortal: "recommendations.dailyEnergyUse.moderate.textPortal"
            },
            high: {
                recommendationText: "recommendations.dailyEnergyUse.high.text",
                recommendationTextPortal: "recommendations.dailyEnergyUse.high.textPortal"
            }
        }[criteria.rating];
    }

    /**
     * Muscle Mass
     */
    static _getMuscleMassRecommendation(criteria) {
        return {
            low: {
                recommendationText: "recommendations.muscleMass.low.text",
                recommendationTextPortal: "recommendations.muscleMass.low.textPortal"
            },
            normal: {
                recommendationText: "recommendations.muscleMass.normal.text",
                recommendationTextPortal: "recommendations.muscleMass.normal.textPortal"
            },
            high: {
                recommendationText: "recommendations.muscleMass.high.text",
                recommendationTextPortal: "recommendations.muscleMass.high.textPortal"
            },
            veryHigh: {
                recommendationText: "recommendations.muscleMass.veryHigh.text",
                recommendationTextPortal: "recommendations.muscleMass.veryHigh.textPortal"
            }
        }[criteria.rating];
    }

    /**
     * WHO-5 Wellbeing
     */
    static _getWellbeingWHO5Recommendation(criteria) {
        return {
            low: {
                recommendationText: "recommendations.wellbeingWHO5.low.text",
                recommendationTextPortal: "recommendations.wellbeingWHO5.low.textPortal"
            },
            moderate: {
                recommendationText: "recommendations.wellbeingWHO5.moderate.text",
                recommendationTextPortal: "recommendations.wellbeingWHO5.moderate.textPortal"
            },
            high: {
                recommendationText: "recommendations.wellbeingWHO5.high.text",
                recommendationTextPortal: "recommendations.wellbeingWHO5.high.textPortal"
            }
        }[criteria.rating];
    }

    /**
     * Musculoskeletal Assessment (MSK)
     */
    static _getMskRecommendation(criteria) {
        return {
            low: {
                recommendationText: "recommendations.msk.low.text",
                recommendationTextPortal: "recommendations.msk.low.textPortal"
            },
            moderate: {
                recommendationText: "recommendations.msk.moderate.text",
                recommendationTextPortal: "recommendations.msk.moderate.textPortal"
            },
            high: {
                recommendationText: "recommendations.msk.high.text",
                recommendationTextPortal: "recommendations.msk.high.textPortal"
            }
        }[criteria.rating];
    }

    /**
     * Women's Health. Station only, so there is no portal copy.
     */
    static _getWomensHealthRecommendation(criteria) {
        return {
            minimal: {
                recommendationText: "recommendations.womens_health.minimal.text",
                recommendationTextPortal: "recommendations.womens_health.minimal.textPortal"
            },
            moderate: {
                recommendationText: "recommendations.womens_health.moderate.text",
                recommendationTextPortal: "recommendations.womens_health.moderate.textPortal"
            },
            significant: {
                recommendationText: "recommendations.womens_health.significant.text",
                recommendationTextPortal: "recommendations.womens_health.significant.textPortal"
            }
        }[criteria.rating];
    }

    /**
     * GPPAQ Physical Activity Index, one recommendation per PAI level.
     */
    static _getGppaqRecommendation(criteria) {
        return ["inactive", "moderatelyInactive", "moderatelyActive", "active"].includes(criteria.rating)
            ? {
                  recommendationText: `recommendations.gppaq.${criteria.rating}.text`,
                  recommendationTextPortal: `recommendations.gppaq.${criteria.rating}.textPortal`
              }
            : undefined;
    }

    static _getLifestyleRecommendation(criteria) {
        return {
            low: {
                recommendationText: "recommendations.lifestyle.low.text",
                recommendationTextPortal: "recommendations.lifestyle.low.textPortal"
            },
            moderate: {
                recommendationText: "recommendations.lifestyle.moderate.text",
                recommendationTextPortal: "recommendations.lifestyle.moderate.textPortal"
            },
            high: {
                recommendationText: "recommendations.lifestyle.high.text",
                recommendationTextPortal: "recommendations.lifestyle.high.textPortal"
            }
        }[criteria.rating];
    }

    /**
     * Per-area lifestyle advice, keyed on area AND rating.
     *
     * The valid pairs are read straight off the guideline rather than restated here. They were
     * two hand-maintained lists until v0.4, and adding a band to one without the other fails
     * silently — the card is simply never requested, or is requested and never resolves.
     *
     * Anything not defined returns undefined, which is the correct answer rather than a miss:
     * smoking at moderate genuinely carries no advice, because the question has no middle answer.
     *
     * NB: getRecommendationFromAdSlotCode builds its whole map eagerly, so this runs for every
     * unrelated slot too, with criteria that have no `area`. It must stay total.
     */
    static _getLifestyleAreaRecommendation(criteria) {
        const defined = LifestyleGuidelineDefinition[criteria.area] || [];

        if (!defined.includes(criteria.rating)) {
            return undefined;
        }

        const key = `${criteria.area}.${criteria.rating}`;

        return {
            recommendationText: `recommendations.lifestyle.areas.${key}.text`,
            recommendationTextPortal: `recommendations.lifestyle.areas.${key}.textPortal`
        };
    }
}
